import { withIDBFallback, IDB_STORES, IDB_TTL } from "./IDBCacheService";
import { getApiBase } from "../helpers/helpers";
import { awaitModelsSeed, getSeedFromMemory } from "./SeedPrefetchService";

/**
 * Fetch the vehicles/availability endpoint with a 4-tier fallback:
 *   1. Fresh IDB cache (within TTL)  → return immediately + background-refresh
 *   2. Live network                  → store in IDB, return
 *   3. Stale IDB (any age)           → return stale data rather than failing
 *   4. In-memory seed                → final fallback for race between seed
 *                                       loading and query execution (offline mode)
 *
 * The function returns the full parsed JSON response object.
 */
export async function fetchVehicleAvailability(
  year: string | number,
  make: string,
  model: string,
): Promise<unknown> {
  // v2_video suffix: always fetch with include_video=1 for this project.
  const idbKey = `vehicle_availability_v2_video_${year}_${make}_${model}`;

  // Ensure the models shard for this year is loaded into both IDB and memory
  // before we check IDB. This prevents the race where the user navigates
  // year → make → model faster than the seed finishes writing to IDB.
  await awaitModelsSeed(year);

  let raw: unknown;
  try {
    raw = await withIDBFallback(
      IDB_STORES.VEHICLE_AVAILABILITY,
      idbKey,
      IDB_TTL.VEHICLE_AVAILABILITY,
      async () => {
        const base = getApiBase();
        const url =
          `${base}/api/v1/vehicles/availability` +
          `?year=${encodeURIComponent(String(year))}` +
          `&make=${encodeURIComponent(String(make))}` +
          `&model=${encodeURIComponent(String(model))}` +
          `&include_video=1`;

        const res = await fetch(url);
        if (!res.ok) throw new Error("Failed to fetch vehicle availability");
        return res.json();
      },
    );
  } catch (err) {
    // 4. Network and IDB both failed — try in-memory seed as last resort
    const seeded = getSeedFromMemory(IDB_STORES.VEHICLE_AVAILABILITY, idbKey);
    if (seeded !== undefined) {
      raw = seeded;
    } else {
      throw err;
    }
  }

  // Normalize shape: the seed stores the inner data directly as
  // { vehicle, availability, configurator } with no outer wrapper,
  // while the live API returns { message, data: { vehicle, availability, configurator } }.
  // Always return the live-API shape so consumers can rely on `response.data.*`.
  if (raw && typeof raw === "object" && !("data" in (raw as object))) {
    return { data: raw };
  }
  return raw;
}

/**
 * Fetch availability for a specific YMM + trim + cab combination.
 * Used to check whether this exact variant has configurator questions.
 * Not cached in IDB — it is a dynamic query driven by live user selection.
 */
export async function fetchVehicleAvailabilityWithTrimCab(
  year: string | number,
  make: string,
  model: string,
  trim: string,
  cab: string,
): Promise<unknown> {
  const base = getApiBase();
  const url =
    `${base}/api/v1/vehicles/availability` +
    `?year=${encodeURIComponent(String(year))}` +
    `&make=${encodeURIComponent(String(make))}` +
    `&model=${encodeURIComponent(String(model))}` +
    `&trim=${encodeURIComponent(String(trim))}` +
    `&cab=${encodeURIComponent(String(cab))}` +
    `&include_video=1`;

  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch trim/cab availability");
  const raw = await res.json();

  if (raw && typeof raw === "object" && !("data" in (raw as object))) {
    return { data: raw };
  }
  return raw;
}
